import React, { useState, useEffect, useMemo } from "react";
import api from "../../../api/axios";
import toast from "react-hot-toast";
import { Check, X, Trash2, Ban, CheckCheck, RefreshCw, CalendarClock, Clock, CalendarCheck2 } from "lucide-react";

// AppointmentStatus enum (API sayı döndürür): 0 Pending, 1 Confirmed, 2 Completed, 3 Cancelled, 4 Rejected
const STATUS = {
  0: { key: "Pending", label: "Beklemede", color: "#f59e0b", bg: "rgba(245,158,11,0.14)" },
  1: { key: "Confirmed", label: "Onaylı", color: "#10b981", bg: "rgba(16,185,129,0.14)" },
  2: { key: "Completed", label: "Tamamlandı", color: "#3b82f6", bg: "rgba(59,130,246,0.14)" },
  3: { key: "Cancelled", label: "İptal", color: "#94a3b8", bg: "rgba(148,163,184,0.14)" },
  4: { key: "Rejected", label: "Reddedildi", color: "#ef4444", bg: "rgba(239,68,68,0.14)" },
};

function statusInfo(raw) {
  if (typeof raw === "number") return STATUS[raw] || STATUS[0];
  const found = Object.values(STATUS).find((s) => s.key === raw);
  return found || STATUS[0];
}

const fmtDate = (ymd) => {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-");
  return `${d}.${m}.${y}`;
};
const fmtTime = (t) => (t ? t.slice(0, 5) : "");

export default function AppointmentListPage() {
  const [appointments, setAppointments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "", employeeId: "", from: "", to: "" });

  const employeeMap = useMemo(() => {
    const m = {};
    employees.forEach((e) => (m[e.employeeId] = e.fullName));
    return m;
  }, [employees]);

  const fetchEmployees = async () => {
    try {
      const res = await api.get("/employee");
      setEmployees(res.data || []);
    } catch {
      /* sessiz */
    }
  };

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.employeeId) params.employeeId = filters.employeeId;
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
      const res = await api.get("/appointment", { params });
      setAppointments(res.data || []);
    } catch {
      toast.error("Randevular yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);
  useEffect(() => {
    fetchAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const changeStatus = async (id, statusKey) => {
    try {
      await api.put(`/appointment/${id}/status`, { status: statusKey });
      toast.success("Durum güncellendi");
      fetchAppointments();
    } catch {
      toast.error("Durum güncellenemedi");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu randevuyu silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/appointment/${id}`);
      toast.success("Randevu silindi");
      fetchAppointments();
    } catch {
      toast.error("Silme başarısız");
    }
  };

  const stats = useMemo(() => {
    const total = appointments.length;
    const pending = appointments.filter((a) => statusInfo(a.status).key === "Pending").length;
    const confirmed = appointments.filter((a) => statusInfo(a.status).key === "Confirmed").length;
    return { total, pending, confirmed };
  }, [appointments]);

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2>Randevular</h2>
          <p className="page-subtitle">{appointments.length} kayıt</p>
        </div>
        <button className="btn btn-ghost" onClick={fetchAppointments}>
          <RefreshCw size={18} /> Yenile
        </button>
      </div>

      {/* İstatistik kartları */}
      <div className="stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 16, marginBottom: 20 }}>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: "rgba(59,130,246,0.14)", color: "#3b82f6" }}><CalendarClock size={22} /></div>
          <div className="stat-info"><div className="stat-value">{stats.total}</div><div className="stat-label">Toplam</div></div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: "rgba(245,158,11,0.14)", color: "#f59e0b" }}><Clock size={22} /></div>
          <div className="stat-info"><div className="stat-value">{stats.pending}</div><div className="stat-label">Beklemede</div></div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: "rgba(16,185,129,0.14)", color: "#10b981" }}><CalendarCheck2 size={22} /></div>
          <div className="stat-info"><div className="stat-value">{stats.confirmed}</div><div className="stat-label">Onaylı</div></div>
        </div>
      </div>

      <div className="content-card">
        {/* Filtreler */}
        <div className="form-row" style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
          <select className="form-select" style={{ maxWidth: 180 }} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
            <option value="">Tüm Durumlar</option>
            {Object.values(STATUS).map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
          <select className="form-select" style={{ maxWidth: 200 }} value={filters.employeeId} onChange={(e) => setFilters({ ...filters, employeeId: e.target.value })}>
            <option value="">Tüm Personel</option>
            {employees.map((e) => (
              <option key={e.employeeId} value={e.employeeId}>{e.fullName}</option>
            ))}
          </select>
          <input type="date" className="form-input" style={{ maxWidth: 170 }} value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} title="Başlangıç" />
          <input type="date" className="form-input" style={{ maxWidth: 170 }} value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} title="Bitiş" />
          {(filters.status || filters.employeeId || filters.from || filters.to) && (
            <button className="btn btn-ghost" onClick={() => setFilters({ status: "", employeeId: "", from: "", to: "" })}>Temizle</button>
          )}
        </div>

        {loading ? (
          <div className="page-loading inline"><div className="loading-spinner" /></div>
        ) : appointments.length === 0 ? (
          <div className="empty-state"><p>Kayıt bulunamadı</p></div>
        ) : (
          <div className="table-wrapper" style={{ overflowX: "auto" }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Tarih</th>
                  <th>Saat</th>
                  <th>Ad Soyad</th>
                  <th>Telefon</th>
                  <th>Personel</th>
                  <th>Durum</th>
                  <th style={{ textAlign: "right" }}>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {appointments.map((a) => {
                  const s = statusInfo(a.status);
                  return (
                    <tr key={a.appointmentId}>
                      <td className="fw-medium">{fmtDate(a.date)}</td>
                      <td>{fmtTime(a.startTime)}–{fmtTime(a.endTime)}</td>
                      <td className="fw-medium">
                        {a.fullName}
                        {a.note ? <div style={{ fontSize: 12, color: "#94a3b8" }}>{a.note}</div> : null}
                      </td>
                      <td>
                        <a href={`tel:${a.phone}`} style={{ color: "inherit" }}>{a.phone}</a>
                        {a.email ? <div style={{ fontSize: 12, color: "#94a3b8" }}>{a.email}</div> : null}
                      </td>
                      <td>{employeeMap[a.employeeId] || `#${a.employeeId}`}</td>
                      <td>
                        <span className="status-badge" style={{ color: s.color, background: s.bg, padding: "4px 10px", borderRadius: 999, fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>{s.label}</span>
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", flexWrap: "wrap" }}>
                          {s.key === "Pending" && (
                            <>
                              <button className="btn-icon" title="Onayla" style={{ color: "#10b981" }} onClick={() => changeStatus(a.appointmentId, "Confirmed")}><Check size={16} /></button>
                              <button className="btn-icon" title="Reddet" style={{ color: "#ef4444" }} onClick={() => changeStatus(a.appointmentId, "Rejected")}><X size={16} /></button>
                            </>
                          )}
                          {s.key === "Confirmed" && (
                            <>
                              <button className="btn-icon" title="Tamamlandı" style={{ color: "#3b82f6" }} onClick={() => changeStatus(a.appointmentId, "Completed")}><CheckCheck size={16} /></button>
                              <button className="btn-icon" title="İptal Et" style={{ color: "#94a3b8" }} onClick={() => changeStatus(a.appointmentId, "Cancelled")}><Ban size={16} /></button>
                            </>
                          )}
                          <button className="btn-icon delete" title="Sil" onClick={() => handleDelete(a.appointmentId)}><Trash2 size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
